<template>
  <div
    ref="overlayEl"
    class="duel-prompt-overlay"
    :class="{
      capturing: captureBackdrop || swallowing,
      compact,
      'place-top': place === 'top',
      'place-mid': place === 'mid',
    }"
    @pointerdown.self="onBackdrop"
    @touchstart="onShield"
    @mousedown="onShield"
  >
    <Transition name="duel-toast">
      <output
        v-if="toast"
        class="duel-prompt-toast"
        :class="toastTone"
      >{{ toast }}</output>
    </Transition>
    <Transition name="duel-card">
      <div
        v-if="message || actions.length"
        class="duel-prompt-card"
        :class="{
          interactive: actions.length > 0,
          bar: actions.length === 0,
          split,
          anchored: !!anchor,
          'anchored-below': !!anchor && !anchor.side && anchor.below,
          'anchored-side-right': anchor?.side === 'right',
          'anchored-side-left': anchor?.side === 'left',
        }"
        :style="anchorStyle"
        @pointerdown.stop
      >
        <p v-if="message" class="duel-prompt-text">{{ message }}</p>
        <div v-if="actions.length" class="duel-prompt-actions" :class="{ row: split }">
          <button
            v-for="action in actions"
            :key="action.id"
            type="button"
            :class="['duel-prompt-btn', action.kind]"
            @pointerdown.stop.prevent="emitAction(action.id, $event)"
            @click.stop.prevent="emitAction(action.id, $event)"
          >
            {{ action.label }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

export interface DuelPromptAction {
  id: string
  label: string
  kind: 'primary' | 'pass'
}

export interface DuelPromptAnchor {
  x: number
  y: number
  below: boolean
  side?: 'left' | 'right'
}

export type DuelToastTone = 'guide' | 'warn' | 'info'

const props = withDefaults(
  defineProps<{
    message: string
    toast: string
    toastTone?: DuelToastTone
    actions: DuelPromptAction[]
    anchor?: DuelPromptAnchor | null
    /** Landscape phone: center on the board, not the full window. */
    compact?: boolean
    /**
     * Park the prompt on a board edge so it never covers the pick row.
     * `mid` sits just under the centered desktop phase HUD (no pick row).
     */
    place?: 'top' | 'bottom' | 'mid'
    /** Two choices share the card width: one on the left, one on the right. */
    split?: boolean
    /**
     * Engine request id of a trap window. A new id means a new question, even
     * when the same trap is listed again.
     */
    questionId?: string
  }>(),
  { toastTone: 'guide', place: 'bottom', split: false, questionId: '' },
)

const emit = defineEmits<{
  action: [id: string]
  dismiss: []
}>()

const anchorStyle = computed(() => {
  if (!props.anchor) return undefined
  return {
    left: `${props.anchor.x}px`,
    top: `${props.anchor.y}px`,
  }
})

/**
 * iPhone/iPad Safari delivers the rest of the tap that opened this menu to
 * whatever is now under the finger. Capturing immediately makes that tap hit
 * the backdrop and the summon/set menu vanishes. Other browsers keep the
 * backdrop live on the same turn.
 */
function isIosBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return iOS || iPadOs
}

const iosTapHold = isIosBrowser()
const overlayEl = ref<HTMLElement | null>(null)
const captureBackdrop = ref(false)
const swallowing = ref(false)
let captureTimer: ReturnType<typeof setTimeout> | null = null
let swallowTimer: ReturnType<typeof setTimeout> | null = null

/**
 * The tap that closes this menu is not finished yet. Keep the scrim up so
 * the rest of it cannot land on the ranked-call note underneath.
 */
function swallowFollowUp(): void {
  swallowing.value = true
  overlayEl.value?.classList.add('capturing')
  if (swallowTimer) clearTimeout(swallowTimer)
  swallowTimer = setTimeout(() => {
    swallowTimer = null
    swallowing.value = false
  }, 450)
}

watch(
  () => !!props.anchor && props.actions.length > 0,
  (on) => {
    if (captureTimer) {
      clearTimeout(captureTimer)
      captureTimer = null
    }
    if (!on) {
      captureBackdrop.value = false
      return
    }
    if (!iosTapHold) {
      captureBackdrop.value = true
      return
    }
    captureBackdrop.value = false
    captureTimer = setTimeout(() => {
      captureTimer = null
      captureBackdrop.value = true
    }, 450)
  },
)

onUnmounted(() => {
  if (captureTimer) clearTimeout(captureTimer)
  if (swallowTimer) clearTimeout(swallowTimer)
  window.removeEventListener('pointerdown', onWindowPointerDown, true)
})

let lastEmitAt = 0
/**
 * Finger-down time of the gesture that is allowed to answer this question.
 * A trap question ignores touches for a short beat after it appears: on a
 * phone the button lands under a finger that was already down, and the
 * browser then sends a second touch onto that button.
 */
let gestureStartedAt = 0
let blockGesturesBefore = 0
/** Touches before this time are the extra tap a phone sends onto a new button. */
let touchArmUntil = 0
/** Compatibility mouse events that follow a touch. They are not a new tap. */
let lastTouchAt = 0
/** Long enough to outlast the extra touch a phone sends after a tap. */
const TRAP_QUESTION_ARM_MS = 400

function onWindowPointerDown(event: PointerEvent): void {
  const now = performance.now()
  if (event.pointerType === 'touch') lastTouchAt = now
  if (event.pointerType === 'mouse' && now - lastTouchAt < 800) return
  if (event.pointerType === 'touch' && now < touchArmUntil) return
  gestureStartedAt = now
}

onMounted(() => {
  window.addEventListener('pointerdown', onWindowPointerDown, true)
})

watch(
  () =>
    `${props.questionId}\n${props.actions.map((action) => action.id).join('\n')}`,
  (signature) => {
    const ids = signature.slice(signature.indexOf('\n') + 1)
    if (!ids) return
    // `pri-` is the engine's trap-window id. Menus and other prompts keep
    // the old rule: only a finger that was already down is ignored.
    const now = performance.now()
    blockGesturesBefore = now
    touchArmUntil = props.questionId.startsWith('pri-') ? now + TRAP_QUESTION_ARM_MS : 0
  },
)

/**
 * pointerdown (mouse) and click (keyboard) both fire for a mouse press; keep
 * a short guard so Activar cannot run twice and still let Enter work.
 * A click whose finger went down before this question appeared is ignored.
 */
function emitAction(id: string, event?: Event): void {
  const now = performance.now()
  const keyboard =
    event?.type === 'click' && event instanceof MouseEvent && event.detail === 0
  if (!keyboard && event?.type === 'pointerdown' && event instanceof PointerEvent) {
    const compatMouse = event.pointerType === 'mouse' && now - lastTouchAt < 800
    // The mouse echo of a touch is not a second answer.
    if (compatMouse) return
    if (event.pointerType === 'touch' && now < touchArmUntil) return
    gestureStartedAt = now
  }
  if (!keyboard && gestureStartedAt < blockGesturesBefore) return
  const wall = Date.now()
  if (wall - lastEmitAt < 400) return
  lastEmitAt = wall
  if (event?.type === 'pointerdown' && props.anchor) swallowFollowUp()
  emit('action', id)
}

/**
 * Clicking empty space around an anchored field menu cancels it. Trap-response
 * prompts have no anchor so the canvas stays clickable.
 */
function onBackdrop(): void {
  if (swallowing.value) return
  if (!props.anchor || !captureBackdrop.value) return
  swallowFollowUp()
  emit('dismiss')
}

/** Only swallow board touches once the menu is actually catching outside taps. */
function onShield(event: Event): void {
  if (!captureBackdrop.value && !swallowing.value) return
  event.stopPropagation()
}
</script>

<style scoped>
/* Sits above the phase-manager HUD (z-index 150). Absolute so it covers the
   board column (not the viewer / compact rails). Trap-response prompts leave
   the canvas clickable; anchored field menus capture so Activar cannot fall
   through to the monster in the same column. Always mounted so leave
   transitions can finish after toast/card clear. */
.duel-prompt-overlay {
  position: absolute;
  inset: 0;
  z-index: 220;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: 76px;
  gap: 12px;
}

.duel-prompt-overlay.place-top {
  justify-content: flex-start;
  padding-top: 68px;
  padding-bottom: 0;
}

/* Top prompts share the column with the toast. A toast that shows up
   later (phase splash, info line) was pushing the card down. Pin the
   card; park the toast on the opposite edge so it cannot shove it. */
.duel-prompt-overlay.place-top .duel-prompt-card:not(.anchored) {
  position: absolute;
  top: 68px;
  left: 50%;
  z-index: 1;
  width: max-content;
  transform: translateX(-50%);
}

.duel-prompt-overlay.compact.place-top .duel-prompt-card:not(.anchored) {
  top: 8px;
}

/* The desktop phase HUD is centered on the board and reaches ~45px below
   the midline (buttons plus the "(You)" tag). Start under it. */
.duel-prompt-overlay.place-mid .duel-prompt-card:not(.anchored) {
  position: absolute;
  top: calc(50% + 64px);
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
}

.duel-prompt-overlay.place-top .duel-prompt-toast {
  position: absolute;
  left: 50%;
  bottom: 16px;
  z-index: 2;
  transform: translateX(-50%);
  pointer-events: none;
}

.duel-prompt-overlay.compact {
  padding: 12px 12px 44px;
}

.duel-prompt-overlay.compact.place-top {
  padding: 8px 12px 12px;
}

.duel-prompt-overlay.capturing {
  pointer-events: auto;
}

.duel-prompt-toast,
.duel-prompt-card {
  background: rgba(12, 14, 24, 0.92);
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  font-family: Roboto, system-ui, sans-serif;
}

.duel-prompt-toast {
  display: block;
  max-width: min(560px, 80vw);
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid rgba(232, 197, 71, 0.35);
  color: #fde68a;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
}

.duel-prompt-toast.warn {
  border-color: color-mix(in srgb, var(--md-sys-color-error) 55%, transparent);
  color: var(--md-sys-color-error);
}

.duel-prompt-toast.info {
  border-color: rgba(226, 232, 240, 0.28);
  color: #e2e8f0;
}

.duel-prompt-card {
  max-width: min(360px, 86vw);
  padding: 16px 18px 14px;
  border-radius: 12px;
  border: 1px solid rgba(232, 197, 71, 0.35);
  text-align: center;
}

.duel-prompt-card.bar {
  max-width: min(480px, 86%);
  padding: 8px 14px;
  pointer-events: none;
}

.duel-prompt-card.interactive {
  pointer-events: auto;
}

.duel-prompt-card.split {
  width: min(480px, calc(100% - 24px));
  max-width: min(480px, calc(100% - 24px));
}

.duel-prompt-overlay.place-top .duel-prompt-card.split:not(.anchored) {
  width: min(480px, calc(100% - 24px));
}

.duel-prompt-card.anchored {
  position: fixed;
  transform: translate(-50%, -100%);
  max-width: 220px;
  padding: 8px;
}

.duel-prompt-card.anchored-below {
  transform: translate(-50%, 0);
}

.duel-prompt-card.anchored-side-right {
  transform: translate(0, -50%);
}

.duel-prompt-card.anchored-side-left {
  transform: translate(-100%, -50%);
}

.duel-prompt-card.anchored .duel-prompt-actions {
  margin-top: 0;
}

.duel-prompt-card.anchored .duel-prompt-btn {
  padding: 6px 10px;
}

.duel-prompt-text {
  margin: 0;
  color: #f8fafc;
  font-size: 15px;
  line-height: 1.35;
}

.duel-prompt-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.duel-prompt-actions.row {
  flex-direction: row;
  align-items: stretch;
}

.duel-prompt-actions.row .duel-prompt-btn {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
}

.duel-prompt-btn {
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.duel-prompt-btn.primary {
  background: #e8c547;
  color: #1a1408;
}

.duel-prompt-btn.primary:hover {
  filter: brightness(1.06);
}

.duel-prompt-btn:active {
  scale: 0.96;
  filter: brightness(0.86);
}

.duel-prompt-btn.pass {
  background: transparent;
  border-color: rgba(226, 232, 240, 0.35);
  color: #e2e8f0;
}

.duel-prompt-btn.pass:hover {
  background: rgba(226, 232, 240, 0.08);
}

.duel-prompt-btn:focus-visible {
  outline: 2px solid #e8c547;
  outline-offset: 2px;
}

.duel-toast-enter-active,
.duel-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.duel-toast-enter-from,
.duel-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* The pinned toast already centers with translateX. Keep that during
   the fade so the line does not jump sideways. */
.duel-prompt-overlay.place-top .duel-toast-enter-from,
.duel-prompt-overlay.place-top .duel-toast-leave-to {
  transform: translateX(-50%) translateY(8px);
}

/* Opacity only — anchored cards already use transform for placement. */
.duel-card-enter-active,
.duel-card-leave-active {
  transition: opacity var(--md-motion-short) var(--md-motion-standard);
}

.duel-card-enter-from,
.duel-card-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .duel-toast-enter-active,
  .duel-toast-leave-active,
  .duel-card-enter-active,
  .duel-card-leave-active {
    transition: none;
  }
}
</style>
